// src/nativeFeatures.ts
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
//import Calendar from './plugins/calendar';
import { Shake } from './plugins/shake';       // custom (see below)

const IS_IOS = Capacitor.getPlatform() === 'ios';

const KEY_UNLOCKED_TODAY = 'trauvo_unlocked_today';
const KEY_DATE = 'trauvo_unlock_date';
const KEY_CURSE_COUNT = 'trauvo_curse_count';
const KEY_TMR_HINT = 'trauvo_tomorrow_hint';
const KEY_TMR_ID = 'trauvo_tomorrow_id';
const KEY_WEATHER_MSG = 'trauvo_weather_msg';

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

async function ensureFreshDay() {
  const { value } = await Preferences.get({ key: KEY_DATE });
  const today = todayStr();
  if (value !== today) {
    await Preferences.set({ key: KEY_DATE, value: today });
    await Preferences.set({ key: KEY_UNLOCKED_TODAY, value: '0' });
    await Preferences.set({ key: KEY_CURSE_COUNT, value: '0' });
  }
}

export async function canGetSuggestionsToday(): Promise<boolean> {
  if (!IS_IOS) return true; // keep Android simple
  await ensureFreshDay();
  const { value } = await Preferences.get({ key: KEY_UNLOCKED_TODAY });
  return value === '1';
}

export async function markUnlockedToday() {
  await Preferences.set({ key: KEY_DATE, value: todayStr() });
  await Preferences.set({ key: KEY_UNLOCKED_TODAY, value: '1' });
}

export async function initNativeFeatures() {
  if (!IS_IOS) return;

  await ensureFreshDay();
  await setupMorningRouletteNotification();
  await setupShakeToUnlock();
  await listenNotificationActions();
}

/* -----------------------------------
 * 1) SHAKE-TO-UNLOCK
 * ----------------------------------- */
async function setupShakeToUnlock() {
  try {
    await Shake.start(); // starts accelerometer monitoring on iOS
    Shake.addListener('shake', async () => {
      // e.g. require N shakes before 7am; keep it simple: any shake before 7am unlocks
      const now = new Date();
      if (now.getHours() < 7) {
        await markUnlockedToday();
        await Haptics.impact({ style: ImpactStyle.Medium });
      }
    });
  } catch (e) {
    console.warn('Shake plugin not available', e);
  }
}

/* -----------------------------------
 * 2) MORNING ROULETTE (7AM YES/NO)
 * ----------------------------------- */

async function setupMorningRouletteNotification() {
  await LocalNotifications.requestPermissions();
  // schedule 7AM local if not already scheduled; for simplicity, schedule daily recurring
  await LocalNotifications.schedule({
    notifications: [
      {
        id: 7001,
        title: "Trauvo Morning Roulette",
        body: "Tap YES in 60 seconds to claim today's adventure.",
        schedule: { repeats: true, every: 'day', at: next7am() },
        actionTypeId: 'TRAUVO_MORNING_ROULETTE',
      },
    ],
  });
}

function next7am(): Date {
  const now = new Date();
  const d = new Date();
  d.setHours(7, 0, 0, 0);
  if (d <= now) d.setDate(d.getDate() + 1);
  return d;
}

async function listenNotificationActions() {
  LocalNotifications.addListener(
    'localNotificationActionPerformed',
    async (event) => {
      if (event.notification.actionTypeId !== 'TRAUVO_MORNING_ROULETTE') return;
      const actionId = event.actionId;
      if (actionId === 'YES') {
        await markUnlockedToday();
        await Haptics.impact({ style: ImpactStyle.Medium });
      }
      if (actionId === 'NO') {
        await Preferences.set({ key: KEY_UNLOCKED_TODAY, value: '0' });
      }
    }
  );
}

/* -----------------------------------
 * 3) LOCATION-LOCKED REVEAL (SIMPLE)
 * ----------------------------------- */

export async function isWithinRadius(
  targetLat: number,
  targetLng: number,
  radiusMeters = 200
): Promise<boolean> {
  if (!IS_IOS) return true;
  const perm = await Geolocation.requestPermissions();
  if (perm.location === 'denied') return false;
  const pos = await Geolocation.getCurrentPosition();
  const dx = (pos.coords.latitude - targetLat) * (Math.PI / 180);
  const dy = (pos.coords.longitude - targetLng) * (Math.PI / 180);
  const a =
    Math.sin(dx / 2) ** 2 +
    Math.cos(targetLat * (Math.PI / 180)) *
      Math.cos(pos.coords.latitude * (Math.PI / 180)) *
      Math.sin(dy / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const dist = 6371000 * c;
  return dist <= radiusMeters;
}

/* -----------------------------------
 * 4) LOCKED TOMORROW (TEASE)
 * ----------------------------------- */

export async function setTomorrowHint(id: string, label: string) {
  // label: full title; store first letter or emoji
  const hint = label.charAt(0);
  await Preferences.set({ key: KEY_TMR_ID, value: id });
  await Preferences.set({ key: KEY_TMR_HINT, value: hint });
}

export async function getTomorrowHint(): Promise<string | null> {
  const { value } = await Preferences.get({ key: KEY_TMR_HINT });
  return value || null;
}

export async function revealTomorrowIfSunrise() {
  // call this on app open; if after 6:30am, clear hint
  const now = new Date();
  if (now.getHours() >= 6) {
    await Preferences.remove({ key: KEY_TMR_HINT });
    // UI decides how to fetch/show full suggestion
  }
}

/* -----------------------------------
 * 5) CURSE JAR
 * ----------------------------------- */

export async function recordSkipAndMaybePunish(): Promise<number> {
  await ensureFreshDay();
  const current = Number(
    (await Preferences.get({ key: KEY_CURSE_COUNT })).value || '0'
  );
  const next = current + 1;
  await Preferences.set({ key: KEY_CURSE_COUNT, value: String(next) });
  await Haptics.impact({ style: ImpactStyle.Light });

  return next;
}

/* -----------------------------------
 * 6) ADD TO CALENDAR
 * ----------------------------------- */

export async function addAdventureToCalendar(
  title: string,
  notes: string,
  start: Date,
  end: Date
) {
  if (!IS_IOS) return;
  try {
  /*  await Calendar.createEvent({
      title,
      notes,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    }); */
  } catch (e) {
    console.warn('Calendar event failed', e);
  }
}

/* -----------------------------------
 * 7) WEATHER-AWARE GREETING (CLIENT)
 * ----------------------------------- */

export async function fetchWeatherMessage(): Promise<string | null> {
  try {
    // replace with your weather endpoint or direct API
    const res = await fetch('/api/weather-summary'); // or your proxy
    if (!res.ok) return null;
    const data = await res.json();
    const msg =
      data.isGoodDay === true
        ? "Looks like a perfect day to explore 🌤️"
        : "Might be a cozy indoor adventure day 🌧️";
    await Preferences.set({ key: KEY_WEATHER_MSG, value: msg });
    return msg;
  } catch {
    return null;
  }
}

export async function getStoredWeatherMessage(): Promise<string | null> {
  const { value } = await Preferences.get({ key: KEY_WEATHER_MSG });
  return value || null;
}

/* -----------------------------------
 * 8) INVITE A FRIEND
 * ----------------------------------- */

export async function inviteFriend() {
  try {
    await Share.share({
      title: 'Come explore with me on Trauvo',
      text: 'Trauvo picks a random adventure for us. Let’s go do this today 👉',
      url: 'https://trauvo.com', // or deep link
      dialogTitle: 'Invite a friend',
    });
  } catch (e) {
    console.warn('Invite failed', e);
  }
}
